import type { DocumentReference } from '../services/schemas/loanServiceSchemas/documentReference';

/**
 * When a document was actually uploaded to the LMS.
 *
 * A document's createdDate is when it was attached to the application in hand, so a proof of
 * address supplied in February reads as uploaded the day a new application is opened.
 * originalUploadDate is carried through reuse and is the real answer; createdDate is only the
 * fallback for references made before the API recorded it.
 */
export const getDocumentUploadDate = (
  doc?: Pick<DocumentReference, 'originalUploadDate' | 'createdDate'> | null
): string | undefined => doc?.originalUploadDate || doc?.createdDate || undefined;

/** Who uploaded the document, falling back to who attached it. */
/** A bare login id (a UUID) says nothing to a reader, so it is not shown as a name. */
const isLoginId = (value?: string | null) =>
  !!value && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value.trim());

export const getDocumentUploader = (
  doc?: Pick<DocumentReference, 'uploadedBy' | 'createdBy'> | null
): string | undefined =>
  [doc?.uploadedBy, doc?.createdBy].find(name => name && !isLoginId(name)) || undefined;

export const formatDocumentDate = (value?: string | null): string => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? 'Unknown'
    : date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

/** "2026-05" -> "May 2026", for a bank statement's period. */
export const formatDocumentPeriod = (period?: string | null): string => {
  if (!period) return '';
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};
